import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';
import { useModalQueue } from './ModalQueueContext';
import { useDailyBonusStatus } from '@/hooks/useDailyBonus';
import { useVerificationStatusQuery } from '@/hooks/queries/useVerificationStatusQuery';
import { dailyBonusEvents } from '@/lib/daily-bonus-events';
import { paymentStore } from '@/stores/paymentStore';
import { detectInAppBrowser } from '@/lib/in-app-browser';
import { isInAppBrowserOverlayDismissed } from '@/components/ProtectedRoute';
import { storage } from '@/lib/storage';

// Check if GeoComply should be bypassed
const shouldSkipGeoComply = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (storage.getItem('SKIP_GEOCOMPLY') === 'true') return true;
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
};

interface StreakBrokenInfo {
  streakBroken: boolean;
  previousStreak: number | null;
}

interface DailyBonusContextType {
  showDailyBonusModal: boolean;
  setShowDailyBonusModal: (show: boolean) => void;
  openDailyBonusModal: () => void;
  dismissDailyBonusModal: () => void;
  // Expose status data for header badge
  canClaim: boolean;
  pendingCount: number;
  enabled: boolean;
  loading: boolean;
  refetch: () => void;
  // Streak broken notification state
  streakBrokenInfo: StreakBrokenInfo | null;
  setStreakBrokenInfo: (info: StreakBrokenInfo | null) => void;
  clearStreakBrokenInfo: () => void;
}

const DailyBonusContext = createContext<DailyBonusContextType | undefined>(undefined);

// Session storage key for dismiss state (clears on browser close, persists on refresh)
const SESSION_KEY = 'daily_bonus_dismissed';

export const useDailyBonus = () => {
  const context = useContext(DailyBonusContext);
  if (context === undefined) {
    throw new Error('useDailyBonus must be used within a DailyBonusProvider');
  }
  return context;
};

const DATA_VERIFICATION_TOAST =
  'Please complete data verification first and then you will be eligible to claim daily bonus.';

export const DailyBonusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, session, isGeoComplyCompliant } = useAuth();
  const { requestShow, notifyDismissed, shouldShow } = useModalQueue();
  const { status, loading, refetch, canClaim, pendingCount, enabled } = useDailyBonusStatus();
  const navigate = useNavigate();
  const { data: verificationData } = useVerificationStatusQuery();
  const isDataVerified = verificationData?.dataVerificationStatus === 'completed';
  const [hasChecked, setHasChecked] = useState(false);
  const [isEligible, setIsEligible] = useState(false);
  const [streakBrokenInfo, setStreakBrokenInfoState] = useState<StreakBrokenInfo | null>(null);

  // Check if we're in an in-app browser
  const isInAppBrowser = useMemo(() => detectInAppBrowser().isInAppBrowser, []);

  // Track if the in-app browser overlay has been dismissed
  const [inAppOverlayDismissed, setInAppOverlayDismissed] = useState(() => {
    return !isInAppBrowser || isInAppBrowserOverlayDismissed();
  });

  // Listen for in-app browser overlay dismissal event
  useEffect(() => {
    if (!isInAppBrowser) return;

    const handleDismissal = () => {
      setInAppOverlayDismissed(true);
    };

    window.addEventListener('inAppBrowserOverlayDismissed', handleDismissal);
    return () => window.removeEventListener('inAppBrowserOverlayDismissed', handleDismissal);
  }, [isInAppBrowser]);

  // Subscribe to streak broken events from processDailyBonusLogin
  useEffect(() => {
    const unsubscribe = dailyBonusEvents.onStreakBroken((payload) => {
      setStreakBrokenInfoState({
        streakBroken: payload.streakBroken,
        previousStreak: payload.previousStreak,
      });
    });

    return unsubscribe;
  }, []);

  // Clear streak broken info when user logs out
  useEffect(() => {
    if (!user) {
      setStreakBrokenInfoState(null);
      dailyBonusEvents.clearPending();
    }
  }, [user]);

  const setStreakBrokenInfo = useCallback((info: StreakBrokenInfo | null) => {
    setStreakBrokenInfoState(info);
  }, []);

  const clearStreakBrokenInfo = useCallback(() => {
    setStreakBrokenInfoState(null);
  }, []);

  // Check if we have received API response
  const hasApiResponse = status !== null;

  // Check session storage for dismiss state
  const isDismissed = useCallback(() => {
    return sessionStorage.getItem(SESSION_KEY) === 'true';
  }, []);

  // Dismiss the modal for this session
  const dismissDailyBonusModal = useCallback(() => {
    sessionStorage.setItem(SESSION_KEY, 'true');
    setIsEligible(false);
    notifyDismissed('DAILY_BONUS');
  }, [notifyDismissed]);

  // Open the modal manually (e.g., from sidebar); only if data verification is complete
  const openDailyBonusModal = useCallback(() => {
    if (!isDataVerified) {
      toast.info(DATA_VERIFICATION_TOAST);
      navigate('/profile');
      return;
    }
    requestShow('DAILY_BONUS');
  }, [isDataVerified, requestShow, navigate]);

  // Auto-show logic: Request to show modal if user has pending rewards
  useEffect(() => {
    // Reset state when user logs out
    if (!user) {
      setHasChecked(false);
      setIsEligible(false);
      sessionStorage.removeItem(SESSION_KEY);
      return;
    }

    // Wait for API response before making any decisions
    if (!hasApiResponse || loading) {
      return;
    }

    // Already evaluated this session
    if (hasChecked) {
      return;
    }

    const canShowInBrowser = !isInAppBrowser || inAppOverlayDismissed;
    const geoComplyPassed = shouldSkipGeoComply() || isGeoComplyCompliant === true;

    // Don't mark as checked yet if GeoComply hasn't completed (null = still checking).
    // The effect will re-run when isGeoComplyCompliant transitions from null to true/false.
    if (!shouldSkipGeoComply() && isGeoComplyCompliant === null) {
      return;
    }
    
    const meetsAllConditions =
      session &&
      user?.email_verified === true &&
      isDataVerified &&
      geoComplyPassed &&
      enabled &&
      canClaim &&
      !isDismissed() &&
      canShowInBrowser;

    if (meetsAllConditions) {
      setIsEligible(true);
      
      // Request to show (queue will handle priority)
      if (!paymentStore.getState().isOpen) {
        requestShow('DAILY_BONUS');
      }
    }
    
    setHasChecked(true);
  }, [user, session, isGeoComplyCompliant, hasChecked, loading, hasApiResponse, isDataVerified, enabled, canClaim, isDismissed, isInAppBrowser, inAppOverlayDismissed, requestShow]);
  
  // Subscribe to payment store - request show when payment closes if eligible
  useEffect(() => {
    let wasOpen = paymentStore.getState().isOpen;
    
    const unsubscribe = paymentStore.subscribe(() => {
      const isOpen = paymentStore.getState().isOpen;
      
      // Payment just closed and we're eligible
      if (wasOpen && !isOpen && isEligible && !isDismissed()) {
        requestShow('DAILY_BONUS');
      }
      
      wasOpen = isOpen;
    });
    
    return unsubscribe;
  }, [isEligible, isDismissed, requestShow]);

  // Get visibility from queue
  const showDailyBonusModal = shouldShow('DAILY_BONUS');

  const value: DailyBonusContextType = {
    showDailyBonusModal,
    setShowDailyBonusModal: () => {}, // Deprecated - use queue system
    openDailyBonusModal,
    dismissDailyBonusModal,
    canClaim,
    pendingCount,
    enabled,
    loading,
    refetch,
    streakBrokenInfo,
    setStreakBrokenInfo,
    clearStreakBrokenInfo,
  };

  return (
    <DailyBonusContext.Provider value={value}>
      {children}
    </DailyBonusContext.Provider>
  );
};
